import { useCallback, useEffect, useRef, useState } from "react";
import { useViimeisin } from "./tuoreus";
import { api, ApiError, type Isometri, type IsometriEsikatselu, type IsometriEsikatseluRivi, type Linja } from "./api";
import { t, virheTeksti, type KaannosAvain } from "./kieli";

/* Päätös 85 vaihe 1: isometrien tuonti massana (PDF:t yksi kerrallaan palvelimelle), ja lista ihmiselle.
   Palvelin lukee nimiöstä linjan, lehden n/m, revision ja piirustusnumeron. Linjaan kohdistetaan vain varma
   tunnistus; epävarma, löytymätön ja nimiötön jäävät tähän listaan ehdotuksineen — ei arvata. Sama tiedosto
   (tavujono) tuodaan kohteeseen kerran. Uudempi revisio korvaa vanhan, vanha jää historiaan. */

const TUNNISTUS: Record<string, KaannosAvain> = {
  varma: "muu.iso.tunn.varma", kasin: "muu.iso.tunn.kasin", epavarma: "muu.iso.tunn.epavarma", ei_loydy: "muu.iso.tunn.ei_loydy",
  ei_nimiota: "muu.iso.tunn.ei_nimiota", ei_tekstia: "muu.iso.tunn.ei_tekstia",
};
const tunnistus = (k: string): string => (TUNNISTUS[k] ? t(TUNNISTUS[k]!) : k);

function base64(f: File): Promise<string> {
  return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result)); r.onerror = () => rej(new Error("luku")); r.readAsDataURL(f); });
}

export default function IsometriTuonti({ kohde, valmis, paakayttaja = false, upotettu = false }: { kohde: number | ""; valmis?: () => void; paakayttaja?: boolean;
  /** T651: napparivin alla, aina auki ilman omaa otsikkoa. */ upotettu?: boolean }) {
  const [naytaPoistetut, setNaytaPoistetut] = useState(false);
  const [poistettuja, setPoistettuja] = useState(0);
  const [aukiTila, setAuki] = useState(false);
  const auki = upotettu || aukiTila;
  const [jono, setJono] = useState<{ tehty: number; yht: number; toistoja: number; virheet: string[] } | null>(null);
  const [rivit, setRivit] = useState<Isometri[] | null>(null);
  const [yhteenveto, setYhteenveto] = useState<{ tunnistus: string; kohdistettu: boolean; n: number }[]>([]);
  const [linjat, setLinjat] = useState<Linja[]>([]);
  const [virhe, setVirhe] = useState<string | null>(null);

  const uusi = useViimeisin();   // päätös 99: kohdistuslista ja linjat samasta, viimeisimmästä kohteesta
  const lataa = useCallback(async () => {
    if (kohde === "") return;
    const tuore = uusi();
    try {
      const [r, l] = await Promise.all([api.isometrit(kohde, "odottaa"), api.linjat(undefined, kohde, 5000)]);
      if (!tuore()) return;
      setRivit(r.rivit); setYhteenveto(r.yhteenveto); setPoistettuja(r.poistettuja ?? 0); setLinjat(l); setVirhe(null);
    } catch (e) { if (!tuore()) return; setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("muu.iso.lataus_virhe")); }
  }, [kohde, uusi]);
  useEffect(() => { if (auki) void lataa(); }, [auki, lataa]);

  async function tuo(tiedostot: FileList | null) {
    if (!tiedostot || kohde === "") return;
    const pdft = [...tiedostot].filter((f) => f.name.toLowerCase().endsWith(".pdf"));
    const tila = { tehty: 0, yht: pdft.length, toistoja: 0, virheet: [] as string[] };
    setJono({ ...tila });
    for (const f of pdft) {
      try {
        // Kansiorakenne nimeen (esim. «Old revisions/…»), jotta lista kertoo mistä tiedosto tuli.
        const nimi = (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name;
        const r = await api.tuoIsometri(kohde, nimi, await base64(f));
        if (r.toisto) tila.toistoja++;
      } catch (e) { tila.virheet.push(`${f.name}: ${e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("muu.iso.ei_onnistunut")}`); }
      tila.tehty++; setJono({ ...tila, virheet: [...tila.virheet] });
    }
    await lataa(); valmis?.();
  }

  const summa = (ehto: (y: { tunnistus: string; kohdistettu: boolean }) => boolean) => yhteenveto.filter(ehto).reduce((a, y) => a + y.n, 0);

  return (
    <section className="panel" data-isometrituonti="">
      {!upotettu && <div className="panel__head" style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0 }}>{t("muu.iso.otsikko")}</h2>
        <button className="nappi nappi--kevyt" aria-expanded={auki} onClick={() => setAuki((a) => !a)}>{auki ? t("hl.sulje") : t("qr.avaa")}</button>
      </div>}
      {auki && (
        <div style={{ padding: "8px 0" }}>
          <p className="dim" style={{ marginTop: 0 }}>
            {t("muu.iso.selite")}
          </p>
          {kohde !== "" && <Pudotus kohde={kohde} valmis={() => { void lataa(); valmis?.(); }} />}
          {kohde === "" ? <p className="huom">{t("muu.iso.valitse_kohde")}</p> : (
            // 7.10.2026: massatuonti tallentaa heti, joten se on suljettuna pudotuksen alla eikä rinnalla (Tonin koe meni tänne).
            <details data-isometri-massatuonti="" style={{ marginTop: 6 }}>
            <summary className="dim">{t("muu.iso.massatuonti")}</summary>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginTop: 6 }}>
              <label className="nappi nappi--kevyt">{t("muu.iso.valitse_kansio")}
                <input type="file" accept="application/pdf" multiple hidden data-isometri-kansio=""
                  {...{ webkitdirectory: "", directory: "" } as Record<string, string>} onChange={(e) => void tuo(e.target.files)} /></label>
              <label className="nappi nappi--kevyt">{t("muu.iso.valitse_tiedostot")}
                <input type="file" accept="application/pdf" multiple hidden data-isometri-tiedostot="" onChange={(e) => void tuo(e.target.files)} /></label>
            </div>
            </details>
          )}
          {jono && (
            <p data-isometri-jono={`${jono.tehty}/${jono.yht}`} style={{ margin: "8px 0" }}>
              {t("muu.iso.tuotu", { n: jono.tehty, m: jono.yht })}{jono.toistoja ? ` · ${t("muu.iso.oli_jo", { n: jono.toistoja })}` : ""}
              {jono.virheet.length ? ` · ${t("muu.iso.epaonnistui", { n: jono.virheet.length })}` : ""}
            </p>
          )}
          {jono?.virheet.map((v) => <p key={v} className="huom huom--vika" style={{ margin: "2px 0" }}>{v}</p>)}
          {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}

          {yhteenveto.length > 0 && (
            <p data-isometri-yhteenveto="" style={{ margin: "8px 0" }}>
              {t("muu.iso.kohteessa")} <b>{summa((y) => y.kohdistettu)}</b> {t("muu.iso.kohdistettu")} · <b>{summa((y) => !y.kohdistettu)}</b> {t("muu.iso.odottaa")}
              {" ("}{yhteenveto.filter((y) => !y.kohdistettu).map((y) => `${tunnistus(y.tunnistus)} ${y.n}`).join(" · ")}{")"}
            </p>
          )}
          {rivit && rivit.length > 0 && (
            <div className="taulu-ikkuna" data-taulu-ikkuna="isometrit-odottaa">
              <table className="tbl" style={{ fontSize: 12.5 }}>
                <thead><tr><th>{t("muu.iso.tiedosto")}</th><th>{t("muu.iso.luettu")}</th><th>{t("muu.iso.tila_syy")}</th><th>{t("muu.iso.kohdistus")}</th></tr></thead>
                <tbody>
                  {rivit.map((r) => <OdottavaRivi key={r.id} r={r} linjat={linjat} valmis={() => void lataa()} />)}
                </tbody>
              </table>
            </div>
          )}
          {rivit && rivit.length === 0 && yhteenveto.length > 0 && <p className="dim">{t("muu.iso.ei_odottavia")}</p>}
          {paakayttaja && kohde !== "" && (
            <div style={{ marginTop: 8 }}>
              <label><input type="checkbox" checked={naytaPoistetut} data-isometri-nayta-poistetut="" onChange={(e) => setNaytaPoistetut(e.target.checked)} /> {t("muu.iso.poistetut", { n: poistettuja })}</label>
              {naytaPoistetut && <Poistetut kohde={kohde} valmis={() => { void lataa(); valmis?.(); }} />}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function OdottavaRivi({ r, linjat, valmis }: { r: Isometri; linjat: Linja[]; valmis: () => void }) {
  const [linja, setLinja] = useState(r.ehdotus_linja ?? "");
  const [lehti, setLehti] = useState(r.lehti_nro ? `${r.lehti_nro}/${r.lehti_kpl ?? ""}` : "");
  const [revisio, setRevisio] = useState(r.revisio ?? "");
  const [viesti, setViesti] = useState<string | null>(null);
  async function kohdista() {
    const l = linjat.find((x) => x.tunnus.toUpperCase() === linja.trim().toUpperCase());
    if (!l) { setViesti(t("muu.iso.valitse_linja")); return; }
    const m = /^(\d{1,2})\s*\/\s*(\d{1,2})?$/.exec(lehti.trim());
    if (lehti.trim() && !m) { setViesti(t("muu.iso.lehti_muoto")); return; }
    try {
      await api.kohdistaIsometri(r.id, { linja_id: l.id,
        ...(m && { lehti_nro: Number(m[1]), lehti_kpl: m[2] ? Number(m[2]) : null }), revisio: revisio.trim() || null });
      valmis();
    } catch (e) { setViesti(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("muu.iso.kohdistus_virhe")); }
  }
  return (
    <tr data-isometri-odottaa={r.id}>
      <td style={{ maxWidth: 220, wordBreak: "break-all" }}>
        {r.liite_id ? <a href={api.liiteOsoite(r.liite_id)} target="_blank" rel="noreferrer">{r.tiedostonimi}</a> : r.tiedostonimi}
      </td>
      <td className="mono" style={{ fontSize: 11.5 }}>
        {r.positio ?? "—"}<br />{t("muu.iso.lehti_pieni")} {r.lehti_nro ?? "?"}{r.lehti_kpl ? `/${r.lehti_kpl}` : ""} · {t("muu.iso.rev")} {r.revisio ?? "–"}
        {r.piirustusnumero ? <><br />{r.piirustusnumero}</> : null}
      </td>
      <td><b>{tunnistus(r.tunnistus)}</b>{r.tunnistus_syy ? <><br /><span className="dim">{r.tunnistus_syy}</span></> : null}</td>
      <td>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "flex-end" }}>
          <label className="dim">{t("hl.linja")}<br />
            <input value={linja} list={`iso-linjat-${r.id}`} onChange={(e) => setLinja(e.target.value)} style={{ width: 200 }} data-kentta="linja" />
            <datalist id={`iso-linjat-${r.id}`}>{linjat.slice(0, 2000).map((l) => <option key={l.id} value={l.tunnus} />)}</datalist></label>
          <label className="dim">{t("isom.lehti")}<br /><input value={lehti} onChange={(e) => setLehti(e.target.value)} style={{ width: 54 }} placeholder="n/m" data-kentta="lehti" /></label>
          <label className="dim">{t("muu.iso.rev_iso")}<br /><input value={revisio} onChange={(e) => setRevisio(e.target.value)} style={{ width: 40 }} data-kentta="revisio" /></label>
          <button className="nappi nappi--kevyt" data-isometri-kohdista={r.id} onClick={() => void kohdista()}>{t("muu.iso.kohdista")}</button>
          <PoistaIsometri iso={r} valmis={valmis} />
        </div>
        {viesti && <p className="huom huom--vika" style={{ margin: "4px 0 0" }}>{viesti}</p>}
      </td>
    </tr>
  );
}

/* ── Päätös 102: pudotus → esikatselu → «Tuo valitut» ───────────────────────────────────────────────────────────────
   pääChatin toimeksianto Tonin välittämänä 6.10.2026: «Esikatselu ennen tallennusta on pakollinen. Mikään ei tallennu
   ilman käyttäjän vahvistusta.» Muoto (Sweco / Vertex-kirja) tunnistetaan palvelimella tiedostosta. Puuttuva linja
   luodaan vain kun «Luo linja» on rastitettu. Osaluettelo näytetään ja tallennetaan isometrin tietoihin; osia ei luoda. */
const LINJATILA: Record<IsometriEsikatseluRivi["linja_tila"], KaannosAvain> = {
  olemassa: "muu.iso.lt.olemassa", puuttuu: "muu.iso.lt.puuttuu", korvattu: "muu.iso.lt.korvattu", epavarma: "muu.iso.lt.epavarma",
};
type Tiedosto = { nimi: string; sisalto: string; e: IsometriEsikatselu | null; virhe: string | null;
  valitut: Record<string, boolean>; luo: Record<string, boolean>; tulokset: Record<string, string> | null; tuodaan: boolean };

/** Rivit joita tässä esikatselussa voi vielä tuoda (ei jo tuotuja eikä tuontikelvottomia). */
const tuotavat = (ti: Tiedosto) => (ti.e?.rivit ?? []).filter((r) => r.voi_tuoda && !r.jo_tuotu_id);

/** «Tuo valitut» kertoo ennen painallusta mitä tapahtuu: «Tuo 9 isometriä · luo 9 linjaa». */
function tuoTeksti(ti: Tiedosto): string {
  const v = tuotavat(ti).filter((r) => ti.valitut[r.avain]);
  const l = v.filter((r) => r.linja_tila === "puuttuu" && ti.luo[r.avain]).length;
  return (v.length === 1 ? t("muu.iso.tuo_yksi") : t("muu.iso.tuo_n", { n: v.length })) + (l ? ` · ${l === 1 ? t("muu.iso.luo_yksi") : t("muu.iso.luo_n", { n: l })}` : "");
}

/** Kolmitilainen koontiruutu: kaikki (rasti) · osa (viiva, indeterminate) · ei mitään. Tila myös sanana data-attribuutissa. */
function Koontiruutu({ nimi, kaikki, osa, poissa, teksti, aseta }:
  { nimi: string; kaikki: boolean; osa: boolean; poissa: boolean; teksti: string; aseta: (arvo: boolean) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const valiin = osa && !kaikki;
  useEffect(() => { if (ref.current) ref.current.indeterminate = valiin; }, [valiin]);
  return (
    <label style={{ whiteSpace: "nowrap", fontWeight: 600 }}>
      <input ref={ref} type="checkbox" checked={kaikki} disabled={poissa} data-koonti={nimi}
        data-koonti-tila={kaikki ? "kaikki" : valiin ? "osa" : "ei"} onChange={() => aseta(!kaikki)} /> {teksti}
    </label>
  );
}

function Pudotus({ kohde, valmis }: { kohde: number; valmis: () => void }) {
  const [tiedostot, setTiedostot] = useState<Tiedosto[]>([]);
  const [paalla, setPaalla] = useState(false);
  const uusi = useViimeisin();   // päätös 99: uusi pudotus hylkää edellisen esikatselun myöhästyneet vastaukset
  async function lue(lista: FileList | File[] | null) {
    if (!lista) return;
    const tuore = uusi();
    const pdft = [...lista].filter((f) => f.name.toLowerCase().endsWith(".pdf"));
    const alku: Tiedosto[] = await Promise.all(pdft.map(async (f) => ({ nimi: f.name, sisalto: await base64(f), e: null, virhe: null,
      valitut: {}, luo: {}, tulokset: null, tuodaan: false })));
    if (!tuore()) return;
    setTiedostot(alku);
    for (const [i, ti] of alku.entries()) {
      try {
        const e = await api.esikatseleIsometri(kohde, ti.nimi, ti.sisalto);
        if (!tuore()) return;
        // Oletus: valittuna vain se mikä voidaan tuoda ja jonka linja on jo kohteessa. Uuden linjan luonti on aina ihmisen rasti.
        const valitut = Object.fromEntries(e.rivit.map((r) => [r.avain, r.voi_tuoda && r.linja_tila === "olemassa"]));
        setTiedostot((v) => v.map((x, j) => j === i ? { ...x, e, valitut } : x));
      } catch (err) {
        if (!tuore()) return;
        setTiedostot((v) => v.map((x, j) => j === i ? { ...x, virhe: err instanceof ApiError ? virheTeksti(err.virhe, err.message) : t("muu.iso.esikatselu_virhe") } : x));
      }
    }
  }
  async function tuo(i: number) {
    const ti = tiedostot[i]; if (!ti?.e) return;
    const valinnat = ti.e.rivit.filter((r) => ti.valitut[r.avain]).map((r) => ({ avain: r.avain, ...(ti.luo[r.avain] ? { luo_linja: true } : {}) }));
    if (!valinnat.length) return;
    setTiedostot((v) => v.map((x, j) => j === i ? { ...x, tuodaan: true } : x));
    try {
      const r = await api.tuoValitutIsometrit(kohde, ti.nimi, ti.sisalto, valinnat);
      const tulokset = Object.fromEntries(r.tulokset.map((x) => [x.avain, x.tulos + (x.linja_luotu ? ` · ${t("muu.iso.linja_luotu")}` : "")]));
      const e = await api.esikatseleIsometri(kohde, ti.nimi, ti.sisalto);
      setTiedostot((v) => v.map((x, j) => j === i ? { ...x, e, tulokset, tuodaan: false, valitut: {}, luo: {} } : x));
      valmis();
    } catch (err) {
      setTiedostot((v) => v.map((x, j) => j === i ? { ...x, tuodaan: false, virhe: err instanceof ApiError ? virheTeksti(err.virhe, err.message) : t("muu.iso.tuonti_virhe") } : x));
    }
  }
  // 7.10.2026 (pääChatin toimeksianto, Tonin välittämä): otsikkorivin koontivalinnat. «Valitse kaikki» koskee rivejä jotka voi
  // valita nyt (puuttuvan linjan rivi vaatii «Luo linja» -rastin); «Luo N linjaa» koskee rivejä joilla linjaa ei ole kohteessa
  // ja valitsee ne samalla, kuten rivin oma rasti.
  const asetaKaikki = (i: number, kentta: "valitut" | "luo", arvo: boolean) =>
    setTiedostot((v) => v.map((x, j) => {
      if (j !== i || !x.e) return x;
      const valitut = { ...x.valitut }, luo = { ...x.luo };
      for (const r of tuotavat(x)) {
        if (kentta === "luo") { if (r.linja_tila === "puuttuu") { luo[r.avain] = arvo; valitut[r.avain] = arvo; } }
        else if (r.linja_tila !== "puuttuu" || luo[r.avain]) valitut[r.avain] = arvo;
      }
      return { ...x, valitut, luo };
    }));
  const aseta = (i: number, kentta: "valitut" | "luo", avain: string, arvo: boolean) =>
    setTiedostot((v) => v.map((x, j) => {
      if (j !== i) return x;
      const uusiX = { ...x, [kentta]: { ...x[kentta], [avain]: arvo } };
      // «Luo linja» valitsee rivin; rastin poisto poistaa myös valinnan (ilman linjaa riviä ei voi tuoda).
      if (kentta === "luo") uusiX.valitut = { ...x.valitut, [avain]: arvo };
      return uusiX;
    }));
  return (
    <div data-isometri-pudotus="" style={{ margin: "0 0 12px" }}>
      <label
        onDragOver={(e) => { e.preventDefault(); setPaalla(true); }} onDragLeave={() => setPaalla(false)}
        onDrop={(e) => { e.preventDefault(); setPaalla(false); void lue(e.dataTransfer.files); }}
        style={{ display: "block", border: `2px dashed ${paalla ? "var(--muste)" : "var(--viiva)"}`, padding: 16, textAlign: "center", cursor: "pointer" }}>
        <b>{t("muu.iso.pudota")}</b> {t("muu.iso.tai_napauta")}
        <br /><span className="dim">{t("muu.iso.pudota_selite")}</span>
        <input type="file" accept="application/pdf" multiple hidden data-isometri-pudotus-tiedosto="" onChange={(e) => void lue(e.target.files)} />
      </label>
      {tiedostot.map((ti, i) => (
        <div key={`${i}-${ti.nimi}`} data-esikatselu-tiedosto={ti.nimi} style={{ marginTop: 10 }}>
          <p style={{ margin: "4px 0" }}><b>{ti.nimi}</b>
            {ti.e ? ` · ${ti.e.muoto === "vertex_kirja" ? t("muu.iso.vertex", { n: ti.e.rivit.length, s: ti.e.sivuja }) : t("muu.iso.sweco")}` : ti.virhe ? "" : ` · ${t("muu.iso.luetaan")}`}</p>
          {ti.virhe && <p className="huom huom--vika" role="alert">{ti.virhe}</p>}
          {ti.e && (
            <>
              <div className="taulu-ikkuna taulu-ikkuna--pino" data-taulu-ikkuna="isometri-esikatselu">
                <table className="tbl pino" style={{ fontSize: 12.5 }}>
                  <thead><tr>
                    <th data-koonti-th="">{(() => { const vv = tuotavat(ti).filter((r) => r.linja_tila !== "puuttuu" || ti.luo[r.avain]);
                      const n = vv.filter((r) => ti.valitut[r.avain]).length;
                      return <Koontiruutu nimi="valitse" kaikki={vv.length > 0 && n === vv.length} osa={n > 0} poissa={!vv.length}
                        teksti={t("muu.iso.valitse_kaikki", { n: vv.length })} aseta={(a) => asetaKaikki(i, "valitut", a)} />; })()}</th>
                    <th data-koonti-th="">{(() => { const lv = tuotavat(ti).filter((r) => r.linja_tila === "puuttuu");
                      const n = lv.filter((r) => ti.luo[r.avain]).length;
                      return lv.length ? <Koontiruutu nimi="luo" kaikki={n === lv.length} osa={n > 0} poissa={false}
                        teksti={lv.length === 1 ? t("muu.iso.luo_yksi_iso") : t("muu.iso.luo_n_iso", { n: lv.length })} aseta={(a) => asetaKaikki(i, "luo", a)} /> : t("hl.linja"); })()}</th>
                    <th>{t("muu.iso.piirustus_rev")}</th><th>{t("muu.iso.sivut")}</th><th>{t("muu.iso.osaluettelo")}</th><th>{t("muu.iso.huomiot")}</th></tr></thead>
                  <tbody>
                    {ti.e.rivit.map((r) => (
                      <tr key={r.avain} data-esikatselu-rivi={r.avain} data-linja-tila={r.linja_tila}>
                        <td data-otsikko="Tuo">
                          {r.jo_tuotu_id ? <span>{t("muu.iso.tuotu_pieni")}</span> : r.voi_tuoda ? (
                            <label><input type="checkbox" checked={!!ti.valitut[r.avain]} data-valitse={r.avain}
                              disabled={r.linja_tila === "puuttuu" && !ti.luo[r.avain]}
                              onChange={(e) => aseta(i, "valitut", r.avain, e.target.checked)} /> {t("muu.iso.valitse")}</label>
                          ) : <span className="dim">{t("muu.iso.ei_voi_tuoda")}</span>}
                        </td>
                        <td className="mono" data-otsikko="Linja">{r.linja ?? "—"}<br /><span className="dim">{t(LINJATILA[r.linja_tila])}{r.ehdotus_linja ? ` · ${t("muu.iso.ehdotus", { s: r.ehdotus_linja })}` : ""}</span>
                          {r.linja_tila === "puuttuu" && r.voi_tuoda && !r.jo_tuotu_id && r.dn && (
                            <><br /><span className={r.dn.arvo ? "" : "dim"} data-dn-luotavalle={r.dn.arvo ?? ""}>{r.dn.huom}</span></>
                          )}
                          {r.linja_tila === "puuttuu" && r.voi_tuoda && !r.jo_tuotu_id && (
                            <><br /><label><input type="checkbox" checked={!!ti.luo[r.avain]} data-luo-linja={r.avain}
                              onChange={(e) => aseta(i, "luo", r.avain, e.target.checked)} /> {t("muu.iso.luo_linja_kohteeseen")}</label></>
                          )}
                        </td>
                        <td className="mono" data-otsikko="Piirustus">{r.piirustusnumero ?? "—"} · {t("muu.iso.rev")} {r.revisio ?? "–"}{r.lehti ? <><br />{t("muu.iso.lehti_pieni")} {r.lehti}</> : null}</td>
                        <td data-otsikko="Sivut">{r.sivu_alku ? (r.sivu_loppu && r.sivu_loppu !== r.sivu_alku ? `${r.sivu_alku}–${r.sivu_loppu}` : `${r.sivu_alku}`) : "—"}</td>
                        <td data-otsikko="Osaluettelo">{r.osaluettelo.length ? <Osaluettelo r={r} /> : <span className="dim">{t("muu.iso.ei_osaluetteloa")}</span>}</td>
                        <td data-otsikko="Huomiot">{ti.tulokset?.[r.avain] ? <b data-tuonti-tulos={r.avain}>{ti.tulokset[r.avain]}</b> : null}
                          {r.syyt.length ? <span className="dim">{ti.tulokset?.[r.avain] ? <br /> : null}{r.syyt.join(" · ")}</span> : null}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button className="nappi" style={{ marginTop: 8 }} data-tuo-valitut={ti.nimi} disabled={ti.tuodaan || !Object.values(ti.valitut).some(Boolean)}
                onClick={() => void tuo(i)}>
                {ti.tuodaan ? t("muu.iso.tuodaan") : tuoTeksti(ti)}
              </button>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

/** Lähde-PDF katkaisee pitkät solut («Cast iron, ...»). Koko tekstiä ei ole tiedostossa — se sanotaan, ei arvata. */
function Teksti({ t: tx }: { t: string | null }) {
  if (!tx) return <>—</>;
  const katkaistu = /(\.\.\.|…)$/.test(tx.trim());
  return katkaistu
    ? <span title={t("muu.iso.katkaistu_title")} data-katkaistu-lahteessa="">{tx} <span className="dim">{t("muu.iso.katkaistu")}</span></span>
    : <>{tx}</>;
}

function Osaluettelo({ r }: { r: IsometriEsikatseluRivi }) {
  const [auki, setAuki] = useState(false);
  const ilman = r.osaluettelo.filter((o) => !o.materiaali).length;
  return (
    <div data-osaluettelo={r.avain}>
      <button className="nappi nappi--kevyt" aria-expanded={auki} onClick={() => setAuki((a) => !a)}>
        {t("muu.iso.osaa", { n: r.osaluettelo.length })}{ilman ? ` · ${t("muu.iso.ilman_materiaalia", { n: ilman })}` : ""} {auki ? "▲" : "▼"}
      </button>
      {auki && (
        <>
        <table className="tbl pino pino--osat" style={{ fontSize: 11.5, marginTop: 4, overflowWrap: "anywhere" }}>
          <thead><tr><th>{t("muu.iso.osa")}</th><th>{t("hl.nimike")}</th><th>{t("muu.lk.kuvaus")}</th><th>{t("muu.iso.standardi")}</th><th>{t("hl.materiaali")}</th><th>{t("muu.iso.kpl")}</th><th>{t("muu.iso.mitat")}</th><th>{t("muu.lmat.pituus")}</th></tr></thead>
          <tbody>
            {r.osaluettelo.map((o) => (
              <tr key={o.osa} data-osa={o.osa}><td data-otsikko="Osa"><b>{o.osa}</b></td><td className="mono" data-otsikko="Nimike"><Teksti t={o.koodi} /></td>
                <td data-otsikko="Kuvaus"><Teksti t={[o.kuvaus, o.kuvaus2].filter(Boolean).join(" · ") || null} /></td><td data-otsikko="Standardi"><Teksti t={o.standardi} /></td>
                <td data-otsikko="Materiaali"><Teksti t={o.materiaali} /></td><td data-otsikko="Kpl">{o.kpl ?? "—"}</td><td className="mono" data-otsikko="Mitat"><Teksti t={o.mitat} /></td>
                <td data-otsikko="Pituus">{o.yksikko === "MM" && o.menekki != null ? `${o.menekki} mm` : "—"}</td></tr>
            ))}
          </tbody>
        </table>
        {/* 7.10.2026 (Tonin koe protot'ssa): Vertexin positionumeroinnissa on aukkoja — rivit puuttuvat jo lähteen osaluettelosta
            (todennettu kuvasta, PIPE000443: 12, 13, 15, 17, 20, 22, 24). Aukko nimetään, jottei sitä lueta lukijan virheeksi. */}
        {(() => { const n = r.osaluettelo.map((o) => o.osa); const max = Math.max(...n); const aukot: number[] = [];
          for (let i = 1; i <= max; i++) if (!n.includes(i)) aukot.push(i);
          return aukot.length ? <p className="dim" data-osaluettelo-aukot={aukot.join(",")} style={{ margin: "4px 0 0" }}>
            {t("muu.iso.aukot", { s: aukot.join(", ") })}</p> : null; })()}
        </>
      )}
    </div>
  );
}

/* ── 201: isometrin pehmeä poisto (Toni 7.10.2026 «pitää saada poistettua, jos tulee väärä») ─────────────────────────
   Sama nappi kohdistamattomalle ja kohdistetulle. Poisto kysyy aina vahvistuksen; jos isometrillä on merkintöjä, palvelin
   kertoo määrän (409 vahvista) ja toinen vahvistus lähettää sen. Tiedosto ja merkinnät säilyvät; palautus pääkäyttäjälle. */
export function PoistaIsometri({ iso, valmis }: { iso: { id: number; tiedostonimi: string }; valmis: () => void }) {
  const [auki, setAuki] = useState(false);
  const [syy, setSyy] = useState("");
  const [nastoja, setNastoja] = useState<number | null>(null);
  const [viesti, setViesti] = useState<string | null>(null);
  const [kay, setKay] = useState(false);
  async function poista() {
    setKay(true); setViesti(null);
    try {
      await api.poistaIsometri(iso.id, { ...(syy.trim() ? { syy: syy.trim() } : {}), ...(nastoja != null ? { vahvistettu: true } : {}) });
      setAuki(false); valmis();
    } catch (e) {
      if (e instanceof ApiError && e.virhe === "vahvista") { setNastoja(Number(/(\d+) merkint/.exec(e.message)?.[1] ?? 0)); setViesti(e.message); }
      else setViesti(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("liite.poisto_virhe"));
    } finally { setKay(false); }
  }
  if (!auki) return <button className="nappi nappi--kevyt" data-isometri-poista={iso.id} onClick={() => setAuki(true)}>{t("liite.poista")}</button>;
  return (
    <div data-isometri-poisto-lomake={iso.id} style={{ border: "1px solid var(--viiva)", padding: 8, marginTop: 4 }}>
      <p style={{ margin: "0 0 6px" }}>{t("isom.poista_kysymys")} <b>{iso.tiedostonimi}</b>? {t("isom.poista_selite")}</p>
      <label className="dim">{t("isom.syy_valinnainen")}<br />
        <input value={syy} onChange={(e) => setSyy(e.target.value)} maxLength={500} style={{ width: "100%" }} data-kentta="poisto-syy" /></label>
      {viesti && <p className="huom huom--vika" role="alert" style={{ margin: "6px 0 0" }}>{viesti}</p>}
      <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
        <button className="nappi" data-isometri-poista-vahvista={iso.id} disabled={kay} onClick={() => void poista()}>
          {nastoja != null ? t("isom.poista_silti", { n: nastoja }) : t("isom.vahvista_poisto")}</button>
        <button className="nappi nappi--kevyt" onClick={() => { setAuki(false); setNastoja(null); setViesti(null); }}>{t("isom.peru")}</button>
      </div>
    </div>
  );
}

function Poistetut({ kohde, valmis }: { kohde: number; valmis: () => void }) {
  const [rivit, setRivit] = useState<Isometri[] | null>(null);
  const [viesti, setViesti] = useState<string | null>(null);
  const uusi = useViimeisin();
  const lataa = useCallback(async () => {
    const tuore = uusi();
    try { const r = await api.isometrit(kohde, "poistetut"); if (tuore()) setRivit(r.rivit); }
    catch (e) { if (tuore()) setViesti(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("muu.iso.lataus_virhe")); }
  }, [kohde, uusi]);
  useEffect(() => { void lataa(); }, [lataa]);
  async function palauta(id: number) {
    try { await api.palautaIsometri(id); await lataa(); valmis(); }
    catch (e) { setViesti(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("muu.iso.palautus_virhe")); }
  }
  return (
    <div data-isometri-poistetut="" style={{ marginTop: 8 }}>
      {viesti && <p className="huom huom--vika" role="alert">{viesti}</p>}
      {rivit && rivit.length === 0 && <p className="dim">{t("muu.iso.ei_poistettuja")}</p>}
      {rivit && rivit.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {rivit.map((r) => (
            <li key={r.id} data-isometri-poistettu={r.id}>
              {r.tiedostonimi}{r.linja ? ` · ${r.linja}` : ""} · {t("muu.iso.poisti")} {r.poistaja ?? "?"} {r.poistettu ? r.poistettu.slice(0, 10) : ""}{r.poisto_syy ? ` · ${r.poisto_syy}` : ""}
              {" "}<button className="nappi nappi--kevyt" data-isometri-palauta={r.id} onClick={() => void palauta(r.id)}>{t("muu.iso.palauta")}</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
